export namespace Tokens {
  export interface TokenSet {
    access: string;
    refresh: string;
    refreshExpiresAt?: string;
  }

  const ACCESS_TOKEN_KEY = 'token';
  const REFRESH_TOKEN_KEY = 'refreshToken';
  const REFRESH_EXPIRES_AT_KEY = 'refreshExpiresAt';
  const TOKEN_TIMESTAMP_KEY = 'tokenTimestamp';
  const REFRESH_CALLED_KEY = 'refreshCalled';
  const PRE_AUTH_TOKEN_KEY = 'preAuthToken';
  const ROLE_KEY = 'userRole';

  /**
   * Store access/refresh token set into `localStorage`.
   * Also resets refresh-related flags/timestamps used by refresh logic.
   */
  export function store(data: TokenSet): boolean {
    if (!validate(data)) {
      return false;
    }
    localStorage.setItem(ACCESS_TOKEN_KEY, data.access);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh);
    if (data.refreshExpiresAt) {
      localStorage.setItem(REFRESH_EXPIRES_AT_KEY, data.refreshExpiresAt);
    }
    // Store token timestamp to track 1-minute expiration for first refresh
    localStorage.setItem(TOKEN_TIMESTAMP_KEY, Date.now().toString());
    // Reset refreshCalled flag on new login
    localStorage.removeItem(REFRESH_CALLED_KEY);
    return true;
  }

  /**
   * Read token set from `localStorage` and validate it.
   * If invalid, clears all token storage and returns null.
   */
  export function getStored(): TokenSet | null {
    const data = {
      access: localStorage.getItem(ACCESS_TOKEN_KEY),
      refresh: localStorage.getItem(REFRESH_TOKEN_KEY),
      refreshExpiresAt: localStorage.getItem(REFRESH_EXPIRES_AT_KEY) || undefined
    };
    const ans = validate(data);
    if (!ans) {
      removeAll();
    }
    return ans;
  }

  /** Validate token set shape (must include access + refresh strings). */
  export function validate(data: any): TokenSet | null {
    const access = data['access'];
    const refresh = data['refresh'];
    if (access && typeof access === 'string' && refresh && typeof refresh === 'string') {
      return {
        access,
        refresh,
        refreshExpiresAt: data['refreshExpiresAt'] || undefined
      };
    }
    return null;
  }

  /** Clear all token-related keys from `localStorage` (access/refresh/role/preAuth/etc.). */
  export function removeAll(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_EXPIRES_AT_KEY);
    localStorage.removeItem(TOKEN_TIMESTAMP_KEY);
    localStorage.removeItem(REFRESH_CALLED_KEY);
    localStorage.removeItem(PRE_AUTH_TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
  }

  /** Store current role in localStorage (used by vendor vs user flows). */
  export function setRole(role: string): void {
    localStorage.setItem(ROLE_KEY, role);
  }

  /** Get stored role from localStorage. */
  export function getRole(): string | null {
    return localStorage.getItem(ROLE_KEY);
  }

  /** Convenience getter for access token. */
  export function forAccess(): string | null {
    return getStored()?.access || null;
  }

  /** Convenience getter for refresh token. */
  export function forRefresh(): string | null {
    return getStored()?.refresh || null;
  }

  /** Read refresh expiry timestamp string (if present). */
  export function getRefreshExpiresAt(): string | null {
    return localStorage.getItem(REFRESH_EXPIRES_AT_KEY);
  }

  /** Read login timestamp used for "first refresh within 1 minute" logic. */
  export function getTokenTimestamp(): string | null {
    return localStorage.getItem(TOKEN_TIMESTAMP_KEY);
  }

  /** Returns true if refresh has already been called since last login. */
  export function isRefreshCalled(): boolean {
    return localStorage.getItem(REFRESH_CALLED_KEY) === 'true';
  }

  /** Mark that refresh was called (prevents repeated refresh attempts in some flows). */
  export function setRefreshCalled(): void {
    localStorage.setItem(REFRESH_CALLED_KEY, 'true');
  }

  /**
   * Update stored tokens (typically after refresh).
   * Also updates timestamp and sets refresh-called flag.
   */
  export function updateToken(token: string, refreshToken?: string, refreshExpiresAt?: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    if (refreshExpiresAt) {
      localStorage.setItem(REFRESH_EXPIRES_AT_KEY, refreshExpiresAt);
    }
    localStorage.setItem(TOKEN_TIMESTAMP_KEY, Date.now().toString());
    setRefreshCalled();
  }

  /** Get pre-auth token (OTP flow) from localStorage. */
  export function getPreAuthToken(): string | null {
    return localStorage.getItem(PRE_AUTH_TOKEN_KEY);
  }

  /** Set pre-auth token (OTP flow) into localStorage. */
  export function setPreAuthToken(token: string): void {
    localStorage.setItem(PRE_AUTH_TOKEN_KEY, token);
  }

  /** Returns true if `refreshExpiresAt` is present and already expired. */
  export function isRefreshExpired(): boolean {
    const refreshExpiresAt = getRefreshExpiresAt();
    if (!refreshExpiresAt) {
      return false;
    }
    const expiresAtTime = new Date(refreshExpiresAt).getTime();
    const currentTime = Date.now();
    return currentTime >= expiresAtTime;
  }
}

